import React from 'react';
import { Pressable } from 'react-native';

import ChevronMobileInactive from '@/assets/images/level-detail/chevron-right-mobile-inactive.svg';
import ChevronMobile from '@/assets/images/level-detail/chevron-right-mobile.svg';
import ChevronTabletInactive from '@/assets/images/level-detail/chevron-right-tablet-inactive.svg';
import ChevronTablet from '@/assets/images/level-detail/chevron-right-tablet.svg';
import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { GuardianPlayerLevelDetailPin } from '@/features/guardian-player/types';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import { LEVEL_DETAIL_STATUS_COLORS } from './constants';
import { LevelDetailPinBadge } from './pin-badge';

type LevelDetailPinRowProps = {
  pin: GuardianPlayerLevelDetailPin;
  selected?: boolean;
  tablet?: boolean;
  showDivider?: boolean;
  onPress?: () => void;
};

export function LevelDetailPinRow({
  pin,
  selected = false,
  tablet = false,
  showDivider = true,
  onPress,
}: LevelDetailPinRowProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const isInactive = pin.status === 'notStarted';
  const statusColor = LEVEL_DETAIL_STATUS_COLORS[pin.status];
  const titleVariant = tablet
    ? 'Font Guardian App/Body/Body 16 Bold'
    : 'Font Guardian App/Subtitle/Subtitle 14 Bold';
  const statusVariant = tablet
    ? 'Font Guardian App/Subtitle/Subtitle 12 Regular'
    : 'Font Guardian App/Subtitle/Subtitle 11 Regular';
  const Chevron = tablet
    ? isInactive
      ? ChevronTabletInactive
      : ChevronTablet
    : isInactive
      ? ChevronMobileInactive
      : ChevronMobile;

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.row,
        tablet ? styles.tabletRow : styles.mobileRow,
        selected ? styles.selectedRow : null,
        pressed ? styles.pressed : null,
      ]}>
      <LevelDetailPinBadge pin={pin} size={scale({ tablet: 48, phone: 32 })} />
      <Stack flex={1} minWidth={0} gap={scale(4)}>
        <Text
          numberOfLines={1}
          variant={titleVariant}
          style={[styles.title, isInactive ? styles.inactiveText : null]}>
          {pin.title}
        </Text>
        <Stack direction="row" align="center" gap={scale(4)}>
          <Text variant={statusVariant} style={[styles.status, { color: statusColor }]}>
            {STRINGS.guardianPlayer.levelDetail.pinStatus[pin.status]}
          </Text>
          {pin.status === 'earned' && pin.date ? (
            <>
              <Stack style={styles.dot} />
              <Text variant={statusVariant} style={styles.date}>
                {pin.date}
              </Text>
            </>
          ) : null}
        </Stack>
      </Stack>
      <Chevron width={scale({ tablet: 24, phone: 20 })} height={scale({ tablet: 24, phone: 20 })} />
      {showDivider ? (
        <Stack style={[styles.divider, tablet ? styles.tabletDivider : null]} />
      ) : null}
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  row: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
  },
  mobileRow: {
    minHeight: scale(38),
  },
  tabletRow: {
    paddingLeft: scale(16),
    paddingRight: scale(4),
    paddingVertical: scale(8),
    minHeight: scale(64),
    borderRadius: scale(12),
  },
  selectedRow: {
    backgroundColor: guardianColors['Main/10'],
  },
  pressed: {
    opacity: 0.72,
  },
  title: {
    color: guardianColors['10'],
  },
  inactiveText: {
    color: guardianColors['5'],
  },
  status: {
    textAlign: 'center',
  },
  dot: {
    width: scale(4),
    height: scale(4),
    borderRadius: scale(2),
    backgroundColor: guardianColors['3'],
  },
  date: {
    color: guardianColors['6'],
    textAlign: 'center',
  },
  divider: {
    position: 'absolute',
    left: scale(40),
    right: 0,
    bottom: scale(-12),
    height: 1,
    backgroundColor: guardianColors['3'],
  },
  tabletDivider: {
    left: 0,
    bottom: scale(-2),
  },
}));
